import { useContext, useEffect, useRef, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Card from "react-bootstrap/Card";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ProductContext } from "../Context/ProductContext";
import { useLanguage } from "../Context/LanguageContext";
import { LikedProductContext } from "../Context/LikedProductContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeart as regularHeart } from "@fortawesome/free-regular-svg-icons";
import { faHeart as solidHeart } from "@fortawesome/free-solid-svg-icons";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import { CartContext } from "../Context/CartContext";
import { useSnackbar } from "notistack";
import { FaSpinner } from "react-icons/fa";
import { useAuth } from "../Context/AuthContext";

function ProductCard() {
  const { loginToken } = useAuth();
  const { language } = useLanguage();
  const { t } = useTranslation();
  const [isFavorited, setIsFavorited] = useState(false);
  const { featuredProducts, fetchFeaturedProducts } =
    useContext(ProductContext);
  const { likedItems, addLikeProduct, removeFromFavorite } =
    useContext(LikedProductContext);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const { cartItems, addToCart, buyNowProduct } = useContext(CartContext);
  const [isAddedToCart, setIsAddedToCart] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const { enqueueSnackbar } = useSnackbar();
  const [loadingState, setLoadingState] = useState({});
  const [addedToCartState, setAddedToCartState] = useState({});
  const [favoriteStates, setFavoriteStates] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      await fetchFeaturedProducts(language);
    };
    fetchData();
  }, [language]);

  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  const toggleFavorite = async (product) => {
    setFavoriteStates((prev) => ({
      ...prev,
      [product.productId]: !prev[product.productId],
    }));

    try {
      if (!favoriteStates[product.productId]) {
        await addLikeProduct(product);
      } else {
        await removeFromFavorite(product);
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
      setFavoriteStates((prev) => ({
        ...prev,
        [product.productId]: !prev[product.productId],
      })); // Revert on error
    }
  };

  // Function to add the product to cart
  const handleAddToCart = async (product) => {
    setLoadingState((prev) => ({ ...prev, [product.productId]: true })); // Start loading for this product

    try {
      const existingCartItem = cartItems.find(
        (item) => item.productId === product.productId
      );

      if (existingCartItem) {
        const updatedQuantity = existingCartItem.quantity + 1;
        await addToCart({
          product: product,
          variant_id: null,
          newQuantity: updatedQuantity,
        });
      } else {
        await addToCart({
          product: product,
          variant_id: null,
          newQuantity: 1,
        });
      }

      setAddedToCartState((prev) => ({ ...prev, [product.productId]: true })); // Mark only this product as added
    } catch (error) {
      console.error("Error adding to cart:", error);
    } finally {
      setLoadingState((prev) => ({ ...prev, [product.productId]: false })); // Stop loading for this product
    }
  };

  const handleBuyNow = async (product) => {
    if (!loginToken) {
      enqueueSnackbar("Kindly Login To your account to buy product instantly", {
        variant: "info",
        autoHideDuration: 2500,
      });
      return;
    }

    if (typeof buyNowProduct !== "function") {
      console.error("buyNowProduct is not a function");
      return;
    }

    if (!product) {
      console.error("Product data is missing");
      return;
    }

    console.log("product: ", product);
    buyNowProduct({
      product: product,
      variant_id: null,
    });
  };

  useEffect(() => {
    const updatedFavorites = featuredProducts.reduce((acc, product) => {
      acc[product.productId] = likedItems.some(
        (item) => item.productId === product.productId
      );
      return acc;
    }, {});

    setFavoriteStates(updatedFavorites);
  }, [likedItems, featuredProducts]);

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: false,
    beforeChange: (_, next) => setCurrentSlide(next),
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          infinite: true,
          dots: false,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          initialSlide: 1,
        },
      },
    ],
  };

  return (
    <>
      <h3 className="container px-4 text-bold">{t("best-products")}</h3>
      <div className="container p-4">
        <Slider ref={sliderRef} {...settings}>
          {featuredProducts.slice(0, 4).map((item) => {
            const realPrice = item?.price || item?.variation_options[0]?.price;
            const higherPrice = (realPrice * 1.2).toFixed(2);

            return (
              <div key={item.productId} className="px-2">
                <Card
                  className="card-hover rounded-4 mb-2 position-relative equal-height-card"
                  data-aos="fade-right"
                >
                  <Link
                    to={`/products/${item.productId}`}
                    state={{ product: item.product }}
                  >
                    <Card.Img
                      variant="top"
                      src={item.image.original}
                      style={{
                        height: "250px",
                        objectFit: "contain",
                        margin: "auto",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    />
                  </Link>
                  <Card.Body className="d-flex flex-column">
                    <div className="price-section pb-4">
                      <span className="off-price">{t("20%-off")}</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center pb-2">
                      <Card.Title>{item.name}</Card.Title>
                      <FontAwesomeIcon
                        icon={
                          favoriteStates[item.productId]
                            ? solidHeart
                            : regularHeart
                        }
                        onClick={() => toggleFavorite(item)}
                      />
                    </div>

                    <div className="price-section pb-3">
                      <span className="hign-price fw-bold fs-5">
                        €{higherPrice}
                      </span>
                    </div>
                    <Card.Text className="text-muted">
                      {item.description.length > 70
                        ? `${item.description.slice(0, 70)}...`
                        : item.description}
                    </Card.Text>
                    <div className="d-flex justify-content-between">
                      <button
                        className="add-to-cart-new me-2 px-3 d-flex align-items-center border-0 fw-bold"
                        onClick={() => {
                          if (item.in_stock && !loadingState[item.productId]) {
                            handleAddToCart(item);
                          } else if (!item.in_stock) {
                            enqueueSnackbar(
                              "⚠️ Currently product is out of stock.",
                              {
                                variant: "danger",
                                autoHideDuration: 1500,
                              }
                            );
                          }
                        }}
                        style={
                          !item.in_stock
                            ? { opacity: 0.5, cursor: "not-allowed" }
                            : {}
                        }
                        disabled={loadingState[item.productId]} // Disable button only for the current product
                      >
                        {loadingState[item.productId] ? (
                          <FaSpinner className="spin" />
                        ) : addedToCartState[item.productId] ? (
                          <>
                            <FontAwesomeIcon
                              icon={["fas", "cart-plus"]}
                              className="d-block d-sm-none"
                            />
                            <span className="d-none d-sm-block">
                              {t("Added-to-cart")}
                            </span>
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon
                              icon={["fas", "cart-plus"]}
                              className="d-block d-sm-none"
                            />
                            <span className="d-none d-sm-block">
                              {t("Add-to-Cart")}
                            </span>
                          </>
                        )}
                      </button>

                      <button
                        className="btn buy-now-new"
                        onClick={() => {
                          if (item.in_stock && !loadingState[item.productId]) {
                            handleBuyNow(item);
                          } else if (!item.in_stock) {
                            enqueueSnackbar(
                              "⚠️ Currently product is out of stock.",
                              {
                                variant: "danger",
                                autoHideDuration: 1500,
                              }
                            );
                          }
                        }}
                        style={
                          !item.in_stock
                            ? { opacity: 0.5, cursor: "not-allowed" }
                            : {}
                        }
                        disabled={loadingState[item.productId]}
                      >
                        Buy Now
                      </button>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            );
          })}
        </Slider>

        {/* Custom Progress Bar & Buttons */}
        <div className="row progressbar-main d-flex justify-content-center align-items-center">
          <button
            className="bg-gray-500 py-1 rounded-l progressbar-button col-2 col-md-1"
            onClick={() => sliderRef.current.slickPrev()}
          >
            <ArrowBackIos />
          </button>

          <div className="progressbar position-relative col-8 col-md-3 bg-gray-300 h-2 rounded ps-0">
            <div
              className="progressbar position-absolute h-full transition-all duration-500 rounded"
              style={{
                width: `${
                  ((currentSlide + 1) / (featuredProducts.length - 1)) * 100
                }%`,
                backgroundColor: "#00713D",
              }}
            />
          </div>

          <button
            className="bg-gray-500 py-1 rounded-r progressbar-button col-1"
            onClick={() => sliderRef.current.slickNext()}
          >
            <ArrowForwardIos />
          </button>
        </div>
      </div>
    </>
  );
}

export default ProductCard;
